const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('admin_token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

// Helper function to handle API responses
const handleResponse = async (response) => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || `HTTP error! status: ${response.status}`);
  }
  return response.json();
};

const api = {
  // Authentication
  async login(credentials) {
    try {
      // For MVP, use mock authentication
      // Replace with actual API call: const response = await fetch(`${API_URL}/auth/login`, {...})
      
      // Mock successful login
      if (credentials.email && credentials.password) {
        return {
          success: true,
          user: {
            id: 1,
            name: 'Admin User',
            email: credentials.email,
            role: 'admin'
          },
          token: 'mock-jwt-token-' + Date.now()
        };
      }
      
      return {
        success: false,
        error: 'Invalid credentials'
      };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  async verifyToken(token) {
    // In production, verify token with backend
    // const response = await fetch(`${API_URL}/auth/verify`, {
    //   headers: { 'Authorization': `Bearer ${token}` }
    // });
    // return handleResponse(response);
    return { valid: true };
  },

  // Dashboard Metrics
  async getDashboardMetrics() {
    try {
      // Replace with actual API call
      // const response = await fetch(`${API_URL}/admin/metrics`, {
      //   headers: getAuthHeaders()
      // });
      // return handleResponse(response);
      
      // Mock data for MVP
      return {
        totalUsers: 2847,
        activeToday: 342,
        monthlyRevenue: 89400,
        aiRequests: 1200000,
        activeSubscriptions: 1847,
        pageViews: {
          total: 89400,
          topPages: [
            { path: '/pricing', views: 12500 },
            { path: '/features', views: 9800 },
            { path: '/about', views: 7200 },
            { path: '/blog', views: 5600 },
            { path: '/contact', views: 3400 }
          ]
        },
        recentSignups: [
          { id: 1, name: 'Sarah Chen', email: 'sarah@example.com', date: new Date().toISOString(), tier: 'Enterprise' },
          { id: 2, name: 'Mike Rodriguez', email: 'mike@example.com', date: new Date(Date.now() - 3600000).toISOString(), tier: 'Professional' },
          { id: 3, name: 'Emma Wilson', email: 'emma@example.com', date: new Date(Date.now() - 7200000).toISOString(), tier: 'SMB' }
        ],
        systemHealth: {
          api: 'healthy',
          database: 'healthy',
          storage: 'healthy'
        }
      };
    } catch (error) {
      console.error('Error fetching dashboard metrics:', error);
      throw error;
    }
  },

  // User Management
  async getUsers(page = 1, limit = 50) {
    try {
      // Replace with actual API call
      // const response = await fetch(`${API_URL}/admin/users?page=${page}&limit=${limit}`, {
      //   headers: getAuthHeaders()
      // });
      // return handleResponse(response);
      
      // Mock data with Phase 1 fields
      return {
        users: [
          { 
            id: 1, 
            name: 'John Doe', 
            email: 'john@example.com', 
            role: 'user', 
            tier: 'professional', 
            status: 'active', 
            createdAt: '2024-01-15',
            company: 'Acme Corp',
            lastLogin: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), // 2 hours ago
            subscriptionStatus: 'active',
            nextBillingDate: '2024-12-01',
            aiRequestsUsed: 450,
            aiRequestsLimit: 1000
          },
          { 
            id: 2, 
            name: 'Jane Smith', 
            email: 'jane@example.com', 
            role: 'admin', 
            tier: 'enterprise', 
            status: 'active', 
            createdAt: '2024-01-20',
            company: 'TechStart Inc',
            lastLogin: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(), // 1 day ago
            subscriptionStatus: 'active',
            nextBillingDate: '2025-01-15',
            aiRequestsUsed: 2500,
            aiRequestsLimit: 10000
          },
          { 
            id: 3, 
            name: 'Bob Johnson', 
            email: 'bob@example.com', 
            role: 'user', 
            tier: 'smb', 
            status: 'active', 
            createdAt: '2024-02-01',
            company: 'Small Biz LLC',
            lastLogin: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(), // 3 days ago
            subscriptionStatus: 'active',
            nextBillingDate: '2024-11-28',
            aiRequestsUsed: 180,
            aiRequestsLimit: 500
          },
          { 
            id: 4, 
            name: 'Sarah Wilson', 
            email: 'sarah@example.com', 
            role: 'user', 
            tier: 'professional', 
            status: 'active', 
            createdAt: '2024-03-10',
            company: 'Design Studio',
            lastLogin: new Date(Date.now() - 5 * 60 * 1000).toISOString(), // 5 minutes ago
            subscriptionStatus: 'active',
            nextBillingDate: '2024-12-10',
            aiRequestsUsed: 850,
            aiRequestsLimit: 1000
          },
          { 
            id: 5, 
            name: 'Mike Chen', 
            email: 'mike@example.com', 
            role: 'user', 
            tier: 'smb', 
            status: 'inactive', 
            createdAt: '2024-02-15',
            company: 'Chen Consulting',
            lastLogin: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days ago
            subscriptionStatus: 'past_due',
            nextBillingDate: 'Overdue',
            aiRequestsUsed: 0,
            aiRequestsLimit: 500
          }
        ],
        total: 2847,
        page,
        totalPages: Math.ceil(2847 / limit)
      };
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  },

  async updateUserRole(userId, role) {
    try {
      // Replace with actual API call
      // const response = await fetch(`${API_URL}/admin/users/${userId}/role`, {
      //   method: 'PATCH',
      //   headers: getAuthHeaders(),
      //   body: JSON.stringify({ role })
      // });
      // return handleResponse(response);
      
      return { success: true, message: 'User role updated' };
    } catch (error) {
      console.error('Error updating user role:', error);
      throw error;
    }
  },

  async updateUserStatus(userId, status) {
    try {
      // Replace with actual API call
      // const response = await fetch(`${API_URL}/admin/users/${userId}/status`, {
      //   method: 'PATCH',
      //   headers: getAuthHeaders(),
      //   body: JSON.stringify({ status })
      // });
      // return handleResponse(response);
      
      return { success: true, message: 'User status updated' };
    } catch (error) {
      console.error('Error updating user status:', error);
      throw error;
    }
  },

  // Activity Logs
  async getActivityLogs(limit = 100) {
    try {
      // Replace with actual API call
      // const response = await fetch(`${API_URL}/admin/activity?limit=${limit}`, {
      //   headers: getAuthHeaders()
      // });
      // return handleResponse(response);
      
      // Mock data
      return {
        logs: [
          { id: 1, user: 'Sarah Chen', action: 'Upgraded to Enterprise', timestamp: new Date().toISOString(), type: 'subscription' },
          { id: 2, user: 'Mike Rodriguez', action: 'Created new campaign', timestamp: new Date(Date.now() - 900000).toISOString(), type: 'campaign' },
          { id: 3, user: 'Emma Wilson', action: 'Payment failed', timestamp: new Date(Date.now() - 3600000).toISOString(), type: 'payment' },
          { id: 4, user: 'John Smith', action: 'API limit reached', timestamp: new Date(Date.now() - 7200000).toISOString(), type: 'api' },
          { id: 5, user: 'Admin User', action: 'Updated portal settings', timestamp: new Date(Date.now() - 10800000).toISOString(), type: 'settings' }
        ]
      };
    } catch (error) {
      console.error('Error fetching activity logs:', error);
      throw error;
    }
  },

  // Feedback
  async getFeedback(status = 'all', limit = 50) {
    try {
      // Replace with actual API call
      // const response = await fetch(`${API_URL}/admin/feedback?status=${status}&limit=${limit}`, {
      //   headers: getAuthHeaders()
      // });
      // return handleResponse(response);
      
      // Mock data
      return {
        feedback: [
          { id: 1, user: 'Sarah Chen', email: 'sarah@example.com', message: 'Love the new AI features!', rating: 5, status: 'new', createdAt: new Date().toISOString() },
          { id: 2, user: 'Mike Rodriguez', email: 'mike@example.com', message: 'Dashboard could be more intuitive', rating: 4, status: 'reviewed', createdAt: new Date(Date.now() - 86400000).toISOString() },
          { id: 3, user: 'Emma Wilson', email: 'emma@example.com', message: 'Need better documentation', rating: 3, status: 'in-progress', createdAt: new Date(Date.now() - 172800000).toISOString() },
          { id: 4, user: 'John Smith', email: 'john@example.com', message: 'Great platform overall!', rating: 5, status: 'resolved', createdAt: new Date(Date.now() - 259200000).toISOString() },
          { id: 5, user: 'Lisa Park', email: 'lisa@example.com', message: 'Would like more integrations', rating: 4, status: 'new', createdAt: new Date(Date.now() - 345600000).toISOString() }
        ]
      };
    } catch (error) {
      console.error('Error fetching feedback:', error);
      throw error;
    }
  },

  // Settings
  async getSettings() {
    try {
      // Replace with actual API call
      // const response = await fetch(`${API_URL}/admin/settings`, {
      //   headers: getAuthHeaders()
      // });
      // return handleResponse(response);
      
      // Mock data
      return {
        portalName: 'Plugly AI',
        maintenanceMode: false,
        allowSignups: true,
        emailNotifications: true,
        apiRateLimit: 1000
      };
    } catch (error) {
      console.error('Error fetching settings:', error);
      throw error;
    }
  },

  async updateSettings(settings) {
    try {
      // Replace with actual API call
      // const response = await fetch(`${API_URL}/admin/settings`, {
      //   method: 'PUT',
      //   headers: getAuthHeaders(),
      //   body: JSON.stringify(settings)
      // });
      // return handleResponse(response);
      
      return { success: true, message: 'Settings updated' };
    } catch (error) {
      console.error('Error updating settings:', error);
      throw error;
    }
  },

  async updateFeedbackStatus(feedbackId, status) {
    try {
      // Replace with actual API call
      // const response = await fetch(`${API_URL}/admin/feedback/${feedbackId}/status`, {
      //   method: 'PATCH',
      //   headers: getAuthHeaders(),
      //   body: JSON.stringify({ status })
      // });
      // return handleResponse(response);
      
      return { success: true, message: 'Feedback status updated' };
    } catch (error) {
      console.error('Error updating feedback status:', error);
      throw error;
    }
  },

  // Page Management
  async getPage(pageId) {
    try {
      // Replace with actual API call
      // const response = await fetch(`${API_URL}/admin/pages/${pageId}`, {
      //   headers: getAuthHeaders()
      // });
      // return handleResponse(response);
      
      // Mock data
      return {
        id: pageId,
        title: pageId === 'new' ? 'New Page' : `Page ${pageId}`,
        slug: pageId,
        content: '<p>Start editing your page content here...</p>',
        seoTitle: '',
        seoDescription: '',
        seoKeywords: '',
        status: 'draft',
        lastEdited: new Date().toISOString()
      };
    } catch (error) {
      console.error('Error fetching page:', error);
      throw error;
    }
  },

  async savePage(pageId, pageData) {
    try {
      // Replace with actual API call
      // const response = await fetch(`${API_URL}/admin/pages/${pageId}`, {
      //   method: 'PUT',
      //   headers: getAuthHeaders(),
      //   body: JSON.stringify(pageData)
      // });
      // return handleResponse(response);
      
      return { success: true, message: 'Page saved successfully' };
    } catch (error) {
      console.error('Error saving page:', error);
      throw error;
    }
  }
};

export default api;
